import React, { useState } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset } from "../../api/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const GENERIC_SUCCESS =
  "If an account exists for that username or email, we sent a reset link.";

/**
 * Request a password-reset email for a main-login user or organization.
 * @returns {JSX.Element}
 */
export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /**
   * @param {React.FormEvent} event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!identifier.trim()) {
      setMissing(true);
      return;
    }
    setSubmitting(true);
    const result = await requestPasswordReset(identifier.trim());
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setSuccess(result.message || GENERIC_SUCCESS);
  };

  return (
    <main className="relative z-10 overflow-x-hidden text-text-primary">
      <section className="relative flex min-h-[calc(100dvh-3.5rem)] w-full flex-col items-center justify-center gap-8 overflow-x-hidden px-4 py-12 text-center md:gap-10 md:py-16">
        <div className="relative mx-auto w-full max-w-4xl">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-hero-glass-border bg-hero-glass-bg px-6 py-8 shadow-staff-card backdrop-blur-sm md:max-w-lg md:px-10 md:py-10">
            <h1 className="mb-3 text-3xl font-bold text-hero-heading md:text-4xl">
              Forgot password
            </h1>
            <p className="mb-8 text-sm text-hero-subheading">
              Enter your username or email and we will send a secure reset link.
            </p>

            {error ? (
              <p
                role="alert"
                className="mb-4 rounded-md border border-error bg-[var(--color-error-bg)] px-3 py-2 text-left text-sm text-error backdrop-blur-3xl"
              >
                {error}
              </p>
            ) : null}

            {success ? (
              <p
                role="status"
                className="mb-4 rounded-md border border-hero-glass-border bg-hero-glass-bg px-3 py-2 text-left text-sm text-hero-heading backdrop-blur-3xl"
              >
                {success}
              </p>
            ) : null}

            <form onSubmit={handleSubmit} noValidate className="space-y-5 text-left">
              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-hero-subheading">
                  Username or email
                  {missing ? (
                    <span className="ml-1 font-medium text-error" aria-live="polite">
                      * required
                    </span>
                  ) : null}
                </Label>
                <Input
                  type="text"
                  name="identifier"
                  id="identifier"
                  aria-required="true"
                  aria-invalid={missing}
                  autoComplete="username"
                  autoFocus
                  value={identifier}
                  onChange={(event) => {
                    setIdentifier(event.target.value);
                    if (missing && event.target.value.trim()) {
                      setMissing(false);
                    }
                  }}
                  className="h-11 border-hero-glass-border bg-hero-glass-bg text-hero-heading shadow-none backdrop-blur-sm focus-visible:ring-ui-ring focus-visible:ring-offset-transparent"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-full bg-hero-cta-primary px-8 font-semibold text-hero-cta-primary-fg shadow-lg transition-[transform,box-shadow] duration-200 ease-out hover:bg-hero-cta-primary-hover motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-xl active:scale-[0.98]"
              >
                {submitting ? "Sending…" : "Send reset link"}
              </Button>
            </form>

            <p className="mt-6 text-sm text-hero-subheading">
              <Link
                to="/login"
                className="font-semibold text-hero-heading underline decoration-hero-heading decoration-2 underline-offset-[5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              >
                Back to login
              </Link>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
